import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Sprout, LayoutDashboard, Truck, MapPin, Scissors, 
  Activity, FileText, Users, LogOut, Menu, X, CalendarDays, DollarSign, Map as MapIcon, CloudRain, Store, ShoppingBag, ExternalLink
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, permKey: 'dashboard' },
  { path: '/activities', label: 'Activities', icon: Activity, permKey: 'activities' },
  { path: '/vehicles', label: 'Vehicles & Inventory', icon: Truck, permKey: 'vehicles' },
  { path: '/locations', label: 'Fields', icon: MapPin, permKey: 'locations' },
  { path: '/turf-cuts', label: 'Turf Cuts', icon: Scissors, permKey: 'turf_cuts' },
  { path: '/reports', label: 'Reports', icon: FileText, permKey: 'reports' },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays, permKey: 'calendar' },
  { path: '/costs', label: 'Costs', icon: DollarSign, permKey: 'costs' },
  { path: '/farm-map', label: 'Farm Map', icon: MapIcon, permKey: 'farm_map' },
  { path: '/rainfall', label: 'Rainfall', icon: CloudRain, permKey: 'dashboard' },
  { path: '/varieties', label: 'Turf Shop', icon: Store, permKey: 'store' },
  { path: '/orders', label: 'Orders', icon: ShoppingBag, permKey: 'store' },
];

const adminItems = [
  { path: '/team', label: 'Users', icon: Users, permKey: 'team' },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const NavLink = ({ item }) => (
    <Link
      to={item.path}
      className={cn(
        'sidebar-link',
        location.pathname === item.path && 'active'
      )}
      onClick={() => setMobileOpen(false)}
      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
    >
      <item.icon className="h-5 w-5" />
      <span>{item.label}</span>
    </Link>
  );

  return (
    <div className="min-h-screen flex bg-background" data-testid="main-layout">
      {/* Mobile Menu Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-card border border-border rounded-md"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-toggle"
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar */}
      <aside 
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-transform duration-200 lg:transform-none',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-border">
            <Link to="/dashboard" className="flex items-center gap-2">
              <Sprout className="h-8 w-8 text-primary" />
              <span className="font-heading font-bold text-xl text-foreground">TurfFarm</span>
            </Link>
          </div>

          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-2">
              {navItems.filter(item => {
                // Determine admin from BOTH role AND permissions (belt-and-suspenders)
                const isAdmin = user?.role === 'admin' || user?.permissions?.includes('team');
                if (isAdmin) return true;
                if (!user?.permissions || user.permissions.length === 0) return true;
                return user.permissions.includes(item.permKey);
              }).map((item) => (
                <NavLink key={item.path} item={item} />
              ))}
              
              {(user?.role === 'admin' || user?.permissions?.includes('team')) && (
                <>
                  <div className="px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4">
                    Admin
                  </div>
                  {adminItems.map((item) => (
                    <NavLink key={item.path} item={item} />
                  ))}
                </>
              )}
            </nav>
          </ScrollArea>

          <div className="p-4 border-t border-border">
            <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary mb-3 px-2" data-testid="view-public-shop-link">
              <ExternalLink className="h-3.5 w-3.5" /> View customer website
            </a>
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{(user?.role === 'admin' || user?.permissions?.includes('team')) ? 'admin' : user?.role}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
            <p className="text-[9px] text-muted-foreground/50 text-center mt-2" data-testid="build-version">v2.6</p>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 lg:ml-0">
        <div className="p-4 md:p-6 lg:p-8 pt-16 lg:pt-8 pb-20 md:pb-8">
          {children}
        </div>
      </main>
    </div>
  );
}
